import moment from "moment/moment";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";

import { admin_bp } from "@/config/roles";
import { Button2 } from "@/ui2/atoms/Button";
import ChevronIcon from "@/assets/images/icons2/icon--chevron.svg?component";
import { cn } from "@/lib/helpers/cn";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import EnvelopeIcon from "@/assets/images/icons2/icon--envelope.svg?component";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { getUsernameFromToken, isAuthorized } from "@/utils/authToken";
import { Link2 } from "@/ui2/atoms/Link";
import { Text, TextTypes } from "@/component-library";
import { useCopyToClipboard } from "@/lib/hooks/useCopyToClipboard";
import UserIcon from "@/assets/images/icons2/icon--user.svg?component";

import { CareTeamMemberCard } from "./components/CareTeamMemberCard";

import "@/component-library/Link/styles.scss";
import "./CareTeam.styles.scss";

export function CareTeam({ careTeamMembers, patientId }) {
  const history = useHistory();
  const copy = useCopyToClipboard();
  const [isHistoryShown, setIsHistoryShown] = useState(false);

  const activeCareTeamMembers = careTeamMembers.filter(
    (careTeamMember) => !careTeamMember.is_deleted,
  );

  const activeCareTeamMembersById = activeCareTeamMembers.reduce((obj, item) => {
    obj[item["provider_id"]] = item;

    return obj;
  }, {});

  const inactiveCareTeamMembers = careTeamMembers
    .filter(
      (careTeamMember) =>
        careTeamMember.is_deleted &&
        // exclude care team members that are still active (this could be old records from a provider email change)
        !Object.hasOwn(activeCareTeamMembersById, careTeamMember.provider_id),
    )
    .sort(
      (a, b) =>
        new Date(b.is_deleted_modified_timestamp) - new Date(a.is_deleted_modified_timestamp),
    );

  const isCurrentUserAdmin =
    isAuthorized("admin:customer_success_executive") ||
    isAuthorized("admin:customer_success_manager") ||
    isAuthorized("admin:admin");

  const internalCareTeamMembers = careTeamMembers
    .map((member) => {
      if (tagTypeMap[member.type] === "internal") {
        return member.email;
      }
    })
    .filter((value) => value !== undefined);

  const renderActiveCareTeamMember = (careTeamMember) => {
    const canScheduleForSelf =
      // intake, BHS and care managers
      (isAuthorized("provider:behavioral") &&
        careTeamMember.provider_id === getUsernameFromToken()) ||
      (isAuthorized("provider:prescribe") && careTeamMember.provider_id === getUsernameFromToken());

    const canScheduleOnBehalfOf =
      ((isCurrentUserAdmin ||
        isAuthorized("provider:peer") ||
        isAuthorized("provider:case_manager")) &&
        ["Prescriber", "Behavioral Specialist", "Intake Behavioral Specialist"].includes(
          careTeamMember.type,
        )) ||
      // intake, BHS and care managers
      (isAuthorized("provider:behavioral") &&
        ["Prescriber", "Behavioral Specialist"].includes(careTeamMember.type));

    const canSchedule = canScheduleForSelf || canScheduleOnBehalfOf;

    return (
      <CareTeamMemberCard
        canSchedule={canSchedule}
        careTeamMember={careTeamMember}
        key={careTeamMember.name}
        onEnvelope={() => copy(careTeamMember.email)}
        onSchedule={() => {
          // the onSchedule function can only be reached if
          // canSchedule is true so each condition here is inherently
          // && with canSchedule
          // we need to check self and behalf of again because
          // we want to make sure each case is handled correctly

          if (canScheduleForSelf) {
            history.push("/app/schedule");
          }
          // new schedule on behalf of view
          else if (
            canScheduleOnBehalfOf &&
            (isAuthorized("provider:peer") || isAuthorized("provider:case_manager"))
          ) {
            history.push(`/app/schedule/${careTeamMember.provider_id}?patientId=${patientId}`);
          }
          // old schedule on behalf of view
          else if (canScheduleOnBehalfOf) {
            history.push(
              "/app/schedule/" +
                careTeamMember.provider_id +
                "?patientId=" +
                patientId +
                "&providerName=" +
                careTeamMember.name +
                "&providerType=" +
                careTeamMember.type,
            );
          }
        }}
        tagType={tagTypeMap[careTeamMember.type] ?? "internal"}
      />
    );
  };

  const renderInactiveCareTeamMember = (careTeamMember) => {
    let careTeamRole = careTeamMember.type;
    const careTeamMemberType = tagTypeMap[careTeamRole] ?? "internal";
    const careTeamMemberTypeLabel = careTeamMemberType === "internal" ? "aptihealth" : "External";
    const isDeletedModifiedTimestamp = careTeamMember.is_deleted_modified_timestamp;

    if (isDeletedModifiedTimestamp) {
      careTeamRole += ` until ${moment.utc(isDeletedModifiedTimestamp).local().format("M/D/YYYY")}`;
    }

    return (
      <div>
        <div>
          <Text className="fs-16 tw:font-bold" type={TextTypes.light}>
            {careTeamMember.name}
          </Text>
        </div>

        <div>
          <Text type={TextTypes.light}>{careTeamMemberTypeLabel}</Text>
        </div>

        <div>
          <Text type={TextTypes.light}>{careTeamRole}</Text>
        </div>

        {careTeamMember?.modality && (
          <div className="CareTeamCard--modality_history_container">
            <Text className="text-break" type={TextTypes.light}>
              {careTeamMember?.modality_other || careTeamMember.modality}
            </Text>

            <div>
              <Text className="d-inline" type={TextTypes.light}>
                {careTeamMember.frequency}
              </Text>

              {careTeamMember.default_appointment_type && (
                <>
                  <Text className="d-inline" type={TextTypes.light}>
                    {" "}
                    &mdash;{" "}
                  </Text>
                  <Text className="d-inline" type={TextTypes.light}>
                    {careTeamMember.default_appointment_time} Min
                  </Text>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <DashboardCard2
      Actions={
        <div className="tw:flex tw:gap-4">
          {internalCareTeamMembers.length > 0 && (
            <Button2
              icon={<EnvelopeIcon />}
              onPress={() => {
                copy(internalCareTeamMembers.join("; "));
              }}
              size="sm"
              variant="link"
            >
              Copy aptihealth Emails
            </Button2>
          )}

          {!isAuthorized("provider:pcp") && !isAuthorized(admin_bp) && (
            <Link2
              href={`/app/patient/${patientId}/care_team`}
              icon={<UserIcon />}
              size="sm"
              {...getAutomationDataAttr("CareTeamCard_manageCareTeam")}
            >
              Manage Care Team
            </Link2>
          )}
        </div>
      }
      title="Care Team"
    >
      <div className="CareTeamCard--members_container">
        {activeCareTeamMembers.map((careTeamMember) => renderActiveCareTeamMember(careTeamMember))}
      </div>

      {inactiveCareTeamMembers.length > 0 && (
        <Button2
          className={cn("tw:mt-4!", isHistoryShown && "tw:mb-4!")}
          icon={!isHistoryShown ? <ChevronIcon /> : <ChevronIcon className="tw:rotate-180" />}
          onPress={() => {
            setIsHistoryShown(!isHistoryShown);
          }}
          size="sm"
          variant="link"
        >
          {!isHistoryShown ? "Show Past Members" : "Hide Past Members"}
        </Button2>
      )}

      {isHistoryShown && (
        <>
          <div className="CareTeamCard--members_container">
            {inactiveCareTeamMembers.map((careTeamMember) =>
              renderInactiveCareTeamMember(careTeamMember),
            )}
          </div>

          <div>
            {/*Release 9.40.0 PR to master: https://github.com/aptihealth/aptihealth-backend/pull/3945/files#diff-6217c699eeaa8cc760d63b3d48c77ea2c1e927b85b5cebc9ec0b84698920f13bR32*/}
            <Text type={TextTypes.light}>
              Note that care team history before 12/21/2023 was not tracked.
            </Text>
          </div>
        </>
      )}
    </DashboardCard2>
  );
}

// this exists to be flexible to other potential
// labels that may arise
const tagTypeMap = {
  "Behavioral Specialist": "internal",
  "Care Manager": "external",
  "Case Manager": "internal",
  "Intake Behavioral Specialist": "internal",
  PCP: "external",

  Peer: "internal",
  Prescriber: "internal",
};
